import { Col, Modal, OverlayTrigger, Popover, Row, Table } from "react-bootstrap";
import { Lab } from '../../amplify/appsync/query';
import { PatientVisit } from '../../amplify/appsync/search-patients';


import { faArrowDown, faArrowUp, faChevronLeft, faChevronRight, faDownLeftAndUpRightToCenter, faMinimize } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import './Labs.css';
import { useEffect, useState } from "react";
import { getUserFriendlyDateStringWithoutSecond } from "../../utils/time";

/**
 * Modal showing labs information
 */
export const LabsModal = (props: {
    showModal: boolean,
    setShowModal: any,
    labs: Lab[][],
    patientVisit?: PatientVisit,
    addAnnotationToPlots?: any,
    highlightedLabTime?: number | null,
}) => {
    const [numCols, setNumCols] = useState<any>(3);

    useEffect(() => {
        setNumCols(3);
    }, [props.showModal]);

    const getLabGroupId = (labGroup: string) => {
        return `lab-group-${labGroup.replace(/[^a-z0-9]/gi, '')}`;
    }

    const handleSetNumCols = (numCols: number, labGroup: string) => {
        setNumCols(numCols);

        setTimeout(() => {
            const element: any = document.getElementById(getLabGroupId(labGroup));
            element.scrollIntoView({behavior: "smooth"});
        }, 500);
    }

    const DetailsPopover = ({lab}: any) => (
        <>
            <Popover.Header className="lab-popover-header">
                {lab.lab_name}
            </Popover.Header>
            <Popover.Body className="lab-popover-body">
                <Row>
                    <Col md={12}>
                        {lab.component_value} {lab.component_unit}
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <small><strong>Reference</strong>: {lab.normal_range_low}-{lab.normal_range_high} {lab.component_unit}</small>
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <small><strong>Collection</strong>: {getUserFriendlyDateStringWithoutSecond(new Date(lab.acquisition_time * 1000))}</small>
                    </Col>
                </Row>
            </Popover.Body>
        </>
    );

    const formatLabGroups = (labGroups: Lab[][]): Lab[][][] => {
        // Lab = one row in a Lab section (e.g. HEMOGLOBIN (HGB))
        // Lab[] = one lab panel (e.g. CBC)
        // Lab[][] = a group of lab panels
        // Lab[][][] = groupings of groups of lab panels (so we can arrange them in rows)
        // 
        const groups: Lab[][][] = [];
        let currRow: Lab[][] = [];
        let maxCols = 6;
        let currNumCols = 0;
        for (let labPanel of labGroups) {
            const uniqueAcquisitionTimes = new Set<number>();
            for (let lab of labPanel) {
                uniqueAcquisitionTimes.add(lab.acquisition_time);
            }
            let numCols = uniqueAcquisitionTimes.size;
            if (currNumCols + numCols > maxCols || labPanel.length > 3) {
                currNumCols = 0;
                groups.push(currRow);
                currRow = [labPanel];
            } else {
                currNumCols += currNumCols + numCols;
                currRow.push(labPanel);
            }
        }
        if (currRow.length > 0) {
            groups.push(currRow);
        }
        return groups;
    }

    const formatLabs = (labs: Lab[]) => {
        if (labs.length === 0) {
            return (<></>)
        }

        const nameToTimeToLabs: any = {};
        const uniqueAcquisitionTimes = new Set<number>();
        for (let lab of labs) {
            uniqueAcquisitionTimes.add(lab.acquisition_time);
        }
        for (let lab of labs) {
            if (!(lab.lab_name in nameToTimeToLabs)) {
                nameToTimeToLabs[lab.lab_name] = {};
            }
            nameToTimeToLabs[lab.lab_name][lab.acquisition_time] = lab;
        }

        // Don't sort because the labs are standardized order
        const labNames: string[] = [...Object.keys(nameToTimeToLabs)];

        const acquisitionTimes: number[] = Array.from(uniqueAcquisitionTimes).sort((a, b) => a - b);


        const showLeftChevron = () => {
            if (!props.highlightedLabTime || (props.highlightedLabTime && !(acquisitionTimes.includes(props.highlightedLabTime)))) {
                // Not showing highlighted time so we are always showing latest lab value
                if (acquisitionTimes.length > 1) {
                    // If there are more than 1 acquisition times, then we should show left chevron
                    // since there is at least one earlier entry
                    return true;
                }
            } else {
                // We are showing a highlighted time so check if there's a lab value prior to the highlighted time
                if (acquisitionTimes.indexOf(props.highlightedLabTime) > 0) {
                    return true;
                }
            }
            return false;
        }

        const showRightChevron = () => {
            if (!props.highlightedLabTime || (props.highlightedLabTime && !(acquisitionTimes.includes(props.highlightedLabTime)))) {
                // Not showing highlighted time so we are always showing latest lab value so there's no right chevron
                return false;
            } else {
                // We are showing a highlighted time so check if there's a lab value after to the highlighted time
                if (acquisitionTimes.indexOf(props.highlightedLabTime) < (acquisitionTimes.length - 1)) {
                    return true;
                }
            }
            return false;
        }

        const getColSpan = () => {
            if (numCols === 1) {
                return 1;
            }

            let tot = 1;
            if (showLeftChevron()) tot++;
            if (showRightChevron()) tot++;
            return tot;
        }

        const isRangeHigh = (lab: Lab) => {
            return lab.normal_range_high && lab.component_value && lab.component_value > lab.normal_range_high;
        }

        const isRangeLow = (lab: Lab) => {
            return lab.normal_range_low && lab.component_value && lab.component_value < lab.normal_range_low;
        }

        const getRangeArrows = (lab: Lab) => {
            // Check status first for granular HIGH/LOW values
            if (lab.status === "CRITICAL_HIGH" || lab.status === "CRITICAL") {
                return " ↑↑";
            } else if (lab.status === "CRITICAL_LOW") {
                return " ↓↓";
            } else if (lab.status === "ABNORMAL_HIGH") {
                return " ↑";
            } else if (lab.status === "ABNORMAL_LOW") {
                return " ↓";
            }

            // Fallback to reference range logic for backwards compatibility
            if (isRangeHigh(lab)) {
                return " ↑";
            } else if (isRangeLow(lab)) {
                return " ↓";
            }

            return "";
        }

        const formatLab = (lab: Lab, acquisitionTime: number, labName: string) => {
            if (!lab) {
                // console.warn("Lab is not available for col ", col)
                return <td
                    key={`${acquisitionTime}-${labName}`}
                    colSpan={getColSpan()}
                ></td>
            }

            return (
                <td key={`${acquisitionTime}-${labName}`} 
                    colSpan={getColSpan()}
                    className={`${acquisitionTime === props.highlightedLabTime ? "lab-highlighted" : ""} lab-highlightable`}
                    onClick={() => {
                        if (props.addAnnotationToPlots && props.patientVisit) {
                            props.addAnnotationToPlots(acquisitionTime, props.patientVisit, "eventMark", `${labName}: ${lab.component_result ? lab.component_result : lab.component_value} ${lab.component_unit && lab.component_unit !== "NaN" && lab.component_unit}${getRangeArrows(lab)}`, 1);
                        }
                        props.setShowModal(false);
                    }}
                >
                    <div>
                        <Row className="list-group-small">
                            <Col md={12}>
                                <OverlayTrigger placement="right" overlay={<Popover id="popover-basic"><DetailsPopover lab={lab} /></Popover>}>

                                    <span className={(lab.status === "ABNORMAL" || lab.status === "CRITICAL" || lab.status === "ABNORMAL_HIGH" || lab.status === "ABNORMAL_LOW" || lab.status === "CRITICAL_HIGH" || lab.status === "CRITICAL_LOW") ? "lab-group-abnormal" : ""}>
                                        {lab.component_result ? lab.component_result : lab.component_value} {lab.component_unit && lab.component_unit !== "NaN" && lab.component_unit}

                                        <>
                                            {
                                                (lab.status === "ABNORMAL_HIGH" || lab.status === "CRITICAL_HIGH" || lab.status === "CRITICAL" || isRangeHigh(lab)) ? (
                                                    <>{' '}
                                                        <FontAwesomeIcon icon={faArrowUp} />
                                                        {(lab.status === "CRITICAL_HIGH" || lab.status === "CRITICAL") && <FontAwesomeIcon icon={faArrowUp} />}
                                                    </>
                                                ) : (lab.status === "ABNORMAL_LOW" || lab.status === "CRITICAL_LOW" || isRangeLow(lab)) ? (
                                                    <>{' '}
                                                        <FontAwesomeIcon icon={faArrowDown} />
                                                        {lab.status === "CRITICAL_LOW" && <FontAwesomeIcon icon={faArrowDown} />}
                                                    </>
                                                ) : (
                                                    <></>
                                                )
                                            }
                                        </>
                                    </span>
                                </OverlayTrigger>
                            </Col>
                        </Row>
                    </div>
                </td>
            )
        }

        const getLatestLab = (labName: string) => {
            const acquisitionTime: number = acquisitionTimes[acquisitionTimes.length - 1];
            const lab: Lab = nameToTimeToLabs[labName][acquisitionTime];
            return lab;
        }
        
        const getHighlightedLab = (labName: string) => {
            if (props.highlightedLabTime) {
                const acquisitionTimeIndex: number = acquisitionTimes.indexOf(props.highlightedLabTime);
                const acquisitionTime = acquisitionTimes[acquisitionTimeIndex];
                const lab: Lab = nameToTimeToLabs[labName][acquisitionTime];
                return lab;
            } else {
                return getLatestLab(labName);
            }
        }

        return (
            <div key={getLabGroupId(labs[0].lab_group)} id={getLabGroupId(labs[0].lab_group)}>
                <Row className="lab-group-table-container">
                    <Col md={12}>
                        <div className="lab-group-header">
                            {labs[0].lab_group}
                            {
                                numCols === 1 && (
                                    <>{' '}<span className="lab-minimize" onClick={() => setNumCols(3)}><FontAwesomeIcon icon={faDownLeftAndUpRightToCenter} /></span></>
                                )
                            }
                        </div>
                        <Table className="lab-group-table">
                            <thead>
                                <tr>
                                    <th>Collection Time</th>
                                    {
                                        numCols === 1 ? (
                                            // Display all labs so no chevron
                                            <></>
                                        ) : (
                                            <>
                                                {
                                                    showLeftChevron() && (
                                                        <th><span className="lab-chevron" onClick={() => handleSetNumCols(1, labs[0].lab_group)}><FontAwesomeIcon icon={faChevronLeft} /></span></th>
                                                    )
                                                }
                                            </>
                                        )
                                    }
                                    {
                                        numCols === 1 ? (
                                            // If one column, show all times
                                            acquisitionTimes.map(header => (
                                                <th key={header}>
                                                    {getUserFriendlyDateStringWithoutSecond(new Date(header * 1000))}
                                                </th>
                                            ))
                                        ) : (
                                            <th>
                                                {
                                                    props.highlightedLabTime ? (
                                                        <>
                                                            {
                                                                acquisitionTimes.includes(props.highlightedLabTime) ? (
                                                                    // This means that the lab was highlighted and we want to display that time if it exists
                                                                    <>{getUserFriendlyDateStringWithoutSecond(new Date(props.highlightedLabTime * 1000))}</>
                                                                ) : (
                                                                    // Otherwise we display the latest lab
                                                                    <>{getUserFriendlyDateStringWithoutSecond(new Date(acquisitionTimes[acquisitionTimes.length - 1] * 1000))}</>
                                                                )
                                                            }
                                                        </>
                                                    ) : (
                                                        // No highlighted time, we display the latest lab
                                                        <>{getUserFriendlyDateStringWithoutSecond(new Date(acquisitionTimes[acquisitionTimes.length - 1] * 1000))}</>
                                                    )
                                                }
                                            </th>
                                        )
                                    }
                                    {
                                        numCols === 1 ? (
                                            // Display al labs so no chevron
                                            <></>
                                        ) : (
                                            <>
                                                {
                                                    showRightChevron() && (
                                                        <th><span className="lab-chevron" onClick={() => handleSetNumCols(1, labs[0].lab_group)}><FontAwesomeIcon icon={faChevronRight} /></span></th>
                                                    )
                                                }
                                            </>
                                        )
                                    }
                                </tr>
                            </thead>
                            <tbody>
                                {labNames.map(labName => (
                                    <tr key={`${labs[0].lab_group}-${labName}`}>
                                        <td key={`${labName}-col`} className="lab-name-col">
                                            {labName}
                                        </td>
                                        {
                                            (numCols === 1) ? (
                                                // If one column, show all times
                                                <>
                                                    {acquisitionTimes.map(acquisitionTime => {
                                                        const lab: Lab = nameToTimeToLabs[labName][acquisitionTime];
                                                        return formatLab(lab, acquisitionTime, labName);
                                                    })}
                                                </>
                                            ) : props.highlightedLabTime === null || (props.highlightedLabTime && !(acquisitionTimes.includes(props.highlightedLabTime))) ? (
                                                // Show latest lab value
                                                <>
                                                    {
                                                        getLatestLab(labName) && formatLab(getLatestLab(labName), getLatestLab(labName).acquisition_time, labName)
                                                    }
                                                </>
                                            ) : (
                                                // Show highlighted time
                                                <>
                                                    {
                                                        formatLab(getHighlightedLab(labName), getHighlightedLab(labName) ? getHighlightedLab(labName).acquisition_time : 0, labName)
                                                    }
                                                </>
                                            )
                                        }
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </div>
        )
    }

    return (
        <Modal show={props.showModal} dialogClassName="modal-90w" onHide={() => props.setShowModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Labs</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* {
                    formatLabGroups(props.labs).map((labPanels: Lab[][]) => {
                        return (
                            <Row>
                                {
                                    labPanels.map(labPanel => (
                                        <Col md={4}>
                                            {
                                                formatLabs(labPanel)
                                            }
                                        </Col>
                                    ))
                                }
                            </Row>
                        )
                    })
                } */}
                <div className={`lab-masonry-${numCols}`}>
                    {
                        props.labs.map((labPanels: Lab[]) => formatLabs(labPanels))
                    }
                </div>
            </Modal.Body>
        </Modal>
    );
};
